/**
 * ═══════════════════════════════════════════════════════════════
 * WASP Frontend v2.0 - Message Trends Chart Component
 * ═══════════════════════════════════════════════════════════════
 * 
 * @file components/MessageTrendsChart.tsx
 * @version 1.0.0
 * @description Gráfico de líneas con tendencias temporales de mensajes
 * @created 2025-10-30 - Fase 2 Session 1
 * 
 * FEATURES:
 * - Gráfico de líneas múltiples con SVG nativo
 * - 3 series: Enviados, Recibidos, Leídos
 * - Selector de periodo (24h/7d/30d)
 * - Tooltips interactivos al hover
 * - Grid de fondo con líneas
 * - Leyenda de colores
 * - Área bajo las curvas (gradient)
 * - Animación de entrada suave
 * - Responsive y escalable
 * - Tema azul consistente
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, ButtonGroup, Button, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine,
  faCircle
} from '@fortawesome/free-solid-svg-icons';

// Types
import {
  MessageTrendsChartProps,
  TrendData,
  TimeRange
} from '../types/performance_types';

// ═══════════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════════

const CHART_CONFIG = {
  padding: { top: 20, right: 20, bottom: 50, left: 50 },
  gridLines: 5,
  pointRadius: 4,
  lineWidth: 2.5,
  animationDuration: 1000
};

const COLORS = {
  sent: '#0d6efd',      // Primary blue
  received: '#0dcaf0',  // Info cyan
  delivered: '#198754', // Success green
  read: '#20c997',      // Teal
  grid: 'var(--bs-border-color)',
  text: 'var(--bs-secondary)'
};

// ═══════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════

interface Point {
  x: number;
  y: number;
  data: TrendData;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  data: TrendData | null;
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

/**
 * Encuentra el valor máximo en los datos
 */
const findMaxValue = (data: TrendData[]): number => {
  let max = 0;
  data.forEach(item => {
    const values = item.values || {};
    const total = (values.sent || 0) + (values.received || 0);
    if (total > max) max = total;
  });
  return max || 100; // Mínimo 100 para evitar divisiones por 0
};

/**
 * Genera puntos para una línea
 */
const generateLinePoints = (
  data: TrendData[],
  key: 'sent' | 'received' | 'read' | 'delivered',
  width: number,
  height: number,
  maxValue: number,
  padding: typeof CHART_CONFIG.padding
): Point[] => {
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  return data.map((item, index) => {
    const value = item.values?.[key] || 0;
    const x = padding.left + (chartWidth / (data.length - 1)) * index;
    const y = padding.top + chartHeight - (value / maxValue) * chartHeight;
    
    return { x, y, data: item };
  });
};

/**
 * Genera path SVG desde puntos
 */
const generatePath = (points: Point[]): string => {
  if (points.length === 0) return '';
  
  let path = `M ${points[0].x},${points[0].y}`;
  
  // Usar curvas suaves (cuadráticas)
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpX = (prev.x + curr.x) / 2;
    
    path += ` Q ${cpX},${prev.y} ${cpX},${(prev.y + curr.y) / 2}`;
    path += ` Q ${cpX},${curr.y} ${curr.x},${curr.y}`;
  }
  
  return path;
};

/**
 * Genera path de área (fill debajo de la línea)
 */
const generateAreaPath = (
  points: Point[],
  height: number,
  padding: typeof CHART_CONFIG.padding
): string => {
  if (points.length === 0) return '';
  
  const linePath = generatePath(points);
  const bottom = height - padding.bottom;
  const lastPoint = points[points.length - 1];
  const firstPoint = points[0];
  
  return `${linePath} L ${lastPoint.x},${bottom} L ${firstPoint.x},${bottom} Z`;
};

/**
 * Formatea el label según el rango de tiempo
 */
const formatLabel = (label: string, timeRange: TimeRange): string => {
  if (timeRange === TimeRange.LAST_24H) {
    return label; // Ya viene como "00:00"
  }
  return label; // Ya viene como "DD/MM"
};

/**
 * Formatea número con separadores
 */
const formatNumber = (num: number): string => {
  return Math.round(num).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// ═══════════════════════════════════════════════════════════════
// CHART COMPONENT
// ═══════════════════════════════════════════════════════════════

const MessageTrendsChart: React.FC<MessageTrendsChartProps> = ({
  data,
  timeRange,
  onTimeRangeChange,
  height = 350,
  showLegend = true,
  loading = false
}) => {

  // ─────────────────────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────────────────────

  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false,
    x: 0,
    y: 0,
    data: null
  });
  
  const [animationProgress, setAnimationProgress] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: height });

  // ─────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────

  /**
   * Maneja resize del contenedor
   */
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setDimensions({ width, height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    
    return () => window.removeEventListener('resize', updateDimensions);
  }, [height]);

  /**
   * Animación de entrada
   */
  useEffect(() => {
    setAnimationProgress(0);
    
    const duration = CHART_CONFIG.animationDuration;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing ease-out
      const eased = 1 - Math.pow(1 - progress, 3);
      setAnimationProgress(eased);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [data, timeRange]);

  // ─────────────────────────────────────────────────────────────
  // CALCULATIONS
  // ─────────────────────────────────────────────────────────────

  const maxValue = findMaxValue(data);
  const { width } = dimensions;
  const { padding } = CHART_CONFIG;

  // Generar puntos para cada serie
  const sentPoints = generateLinePoints(data, 'sent', width, height, maxValue, padding);
  const receivedPoints = generateLinePoints(data, 'received', width, height, maxValue, padding);
  const readPoints = generateLinePoints(data, 'read', width, height, maxValue, padding);

  // Generar paths
  const sentPath = generatePath(sentPoints);
  const receivedPath = generatePath(receivedPoints);
  const readPath = generatePath(readPoints);

  // Generar paths de área
  const sentAreaPath = generateAreaPath(sentPoints, height, padding);
  const receivedAreaPath = generateAreaPath(receivedPoints, height, padding);

  // ─────────────────────────────────────────────────────────────
  // HANDLERS
  // ─────────────────────────────────────────────────────────────

  /**
   * Maneja el hover sobre el gráfico
   */
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!containerRef.current || sentPoints.length === 0) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Encontrar el punto más cercano usando reduce
    const result = sentPoints.reduce<{ point: Point | null; distance: number }>(
      (acc, point) => {
        const distance = Math.abs(point.x - mouseX);
        if (distance < acc.distance && distance < 30) {
          return { point, distance };
        }
        return acc;
      },
      { point: null, distance: Infinity }
    );

    if (result.point) {
      setTooltip({
        visible: true,
        x: result.point.x,
        y: mouseY,
        data: result.point.data
      });
    } else {
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  /**
   * Maneja cuando el mouse sale del gráfico
   */
  const handleMouseLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Card style={{
        border: 'none',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        backgroundColor: 'var(--bs-body-bg)',
        height: height + 100
      }}>
        <Card.Body className="d-flex align-items-center justify-content-center">
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted mb-0">Cargando tendencias...</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card style={{
      border: 'none',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: 'var(--bs-body-bg)'
    }}>
      <Card.Body className="p-4">
        
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h5 className="mb-1" style={{ fontWeight: '600' }}>
              <FontAwesomeIcon icon={faChartLine} className="me-2" style={{ color: COLORS.sent }} />
              Tendencias de Mensajes
            </h5>
            <small className="text-muted">
              {data.length} puntos de datos
            </small>
          </div>

          {/* Selector de periodo */}
          <ButtonGroup size="sm">
            <Button
              variant={timeRange === TimeRange.LAST_24H ? 'primary' : 'outline-primary'}
              onClick={() => onTimeRangeChange(TimeRange.LAST_24H)}>
              24h
            </Button>
            <Button
              variant={timeRange === TimeRange.LAST_7D ? 'primary' : 'outline-primary'}
              onClick={() => onTimeRangeChange(TimeRange.LAST_7D)}>
              7d
            </Button>
            <Button
              variant={timeRange === TimeRange.LAST_30D ? 'primary' : 'outline-primary'}
              onClick={() => onTimeRangeChange(TimeRange.LAST_30D)}>
              30d
            </Button>
          </ButtonGroup>
        </div>

        {/* SVG Chart */}
        <div ref={containerRef} style={{ position: 'relative' }}>
          <svg
            width={width}
            height={height}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{ cursor: 'crosshair' }}>
            
            {/* Definiciones de gradientes */}
            <defs>
              <linearGradient id="sentGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={COLORS.sent} stopOpacity="0.3" />
                <stop offset="100%" stopColor={COLORS.sent} stopOpacity="0.05" />
              </linearGradient>
              <linearGradient id="receivedGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={COLORS.received} stopOpacity="0.3" />
                <stop offset="100%" stopColor={COLORS.received} stopOpacity="0.05" />
              </linearGradient>
            </defs>

            {/* Grid horizontal */}
            {Array.from({ length: CHART_CONFIG.gridLines + 1 }).map((_, i) => {
              const y = padding.top + ((height - padding.top - padding.bottom) / CHART_CONFIG.gridLines) * i;
              const value = maxValue - (maxValue / CHART_CONFIG.gridLines) * i;
              
              return (
                <g key={`grid-h-${i}`}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={width - padding.right}
                    y2={y}
                    stroke={COLORS.grid}
                    strokeWidth="1"
                    opacity="0.3"
                  />
                  <text
                    x={padding.left - 10}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill={COLORS.text}
                    fontSize="11"
                    fontWeight="500">
                    {Math.round(value)}
                  </text>
                </g>
              );
            })}

            {/* Grid vertical y labels */}
            {data.map((item, index) => {
              // Mostrar solo algunos labels para no saturar
              const showLabel = data.length <= 10 || index % Math.ceil(data.length / 10) === 0;
              if (!showLabel) return null;

              const x = padding.left + ((width - padding.left - padding.right) / (data.length - 1)) * index;
              const y = height - padding.bottom;

              return (
                <g key={`grid-v-${index}`}>
                  <line
                    x1={x}
                    y1={padding.top}
                    x2={x}
                    y2={y}
                    stroke={COLORS.grid}
                    strokeWidth="1"
                    opacity="0.2"
                  />
                  <text
                    x={x}
                    y={y + 20}
                    textAnchor="middle"
                    fill={COLORS.text}
                    fontSize="11"
                    fontWeight="500">
                    {formatLabel(item.label, timeRange)}
                  </text>
                </g>
              );
            })}

            {/* Áreas con gradiente */}
            <g style={{ 
              clipPath: `inset(0 ${100 - animationProgress * 100}% 0 0)`,
              transition: 'clip-path 0.3s'
            }}>
              <path
                d={sentAreaPath}
                fill="url(#sentGradient)"
              />
              <path
                d={receivedAreaPath}
                fill="url(#receivedGradient)"
              />
            </g>

            {/* Líneas */}
            <g style={{ 
              clipPath: `inset(0 ${100 - animationProgress * 100}% 0 0)`,
              transition: 'clip-path 0.3s'
            }}>
              {/* Línea de enviados */}
              <path
                d={sentPath}
                fill="none"
                stroke={COLORS.sent}
                strokeWidth={CHART_CONFIG.lineWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              
              {/* Línea de recibidos */}
              <path
                d={receivedPath}
                fill="none"
                stroke={COLORS.received}
                strokeWidth={CHART_CONFIG.lineWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Línea de leídos */}
              <path
                d={readPath}
                fill="none"
                stroke={COLORS.read}
                strokeWidth={CHART_CONFIG.lineWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>

            {/* Puntos en hover */}
            {tooltip.visible && tooltip.data && (
              <g>
                {/* Línea vertical */}
                <line
                  x1={tooltip.x}
                  y1={padding.top}
                  x2={tooltip.x}
                  y2={height - padding.bottom}
                  stroke={COLORS.grid}
                  strokeWidth="1"
                  strokeDasharray="4,4"
                  opacity="0.5"
                />

                {/* Puntos */}
                {[
                  { color: COLORS.sent, key: 'sent' },
                  { color: COLORS.received, key: 'received' },
                  { color: COLORS.read, key: 'read' }
                ].map(({ color, key }) => {
                  const points = key === 'sent' ? sentPoints : 
                                 key === 'received' ? receivedPoints : readPoints;
                  const point = points.find(p => p.data === tooltip.data);
                  
                  if (!point) return null;

                  return (
                    <g key={key}>
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={CHART_CONFIG.pointRadius + 2}
                        fill="var(--bs-body-bg)"
                        stroke={color}
                        strokeWidth="2"
                      />
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r={CHART_CONFIG.pointRadius}
                        fill={color}
                      />
                    </g>
                  );
                })}
              </g>
            )}

          </svg>

          {/* Tooltip */}
          {tooltip.visible && tooltip.data && (
            <div
              style={{
                position: 'absolute',
                left: tooltip.x + 10,
                top: tooltip.y - 80,
                backgroundColor: 'var(--bs-dark)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                pointerEvents: 'none',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 1000,
                minWidth: '150px'
              }}>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                {formatLabel(tooltip.data.label, timeRange)}
              </div>
              <div style={{ color: COLORS.sent }}>
                Enviados: {formatNumber(tooltip.data.values?.sent || 0)}
              </div>
              <div style={{ color: COLORS.received }}>
                Recibidos: {formatNumber(tooltip.data.values?.received || 0)}
              </div>
              <div style={{ color: COLORS.read }}>
                Leídos: {formatNumber(tooltip.data.values?.read || 0)}
              </div>
            </div>
          )}
        </div>

        {/* Leyenda */}
        {showLegend && (
          <div className="d-flex justify-content-center gap-4 mt-4">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faCircle} 
                style={{ color: COLORS.sent, fontSize: '10px' }}
                className="me-2"
              />
              <small style={{ fontWeight: '500' }}>Enviados</small>
            </div>
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faCircle} 
                style={{ color: COLORS.received, fontSize: '10px' }}
                className="me-2"
              />
              <small style={{ fontWeight: '500' }}>Recibidos</small>
            </div>
            <div className="d-flex align-items-center">
              <FontAwesomeIcon 
                icon={faCircle} 
                style={{ color: COLORS.read, fontSize: '10px' }}
                className="me-2"
              />
              <small style={{ fontWeight: '500' }}>Leídos</small>
            </div>
          </div>
        )}

      </Card.Body>
    </Card>
  );
};

export default MessageTrendsChart;